/**
 * Curriculum v2 story finale on the map (docs/CURRICULUM-V2.md §B): when a world is complete its story
 * ends with a short moment — flipbook (warm-up sketchbook), concert (animals), season wheel (nature),
 * parade (toy town), lanterns (night city), party (kitchen) — made of the child's own drawings of the
 * world. Pure CSS animation; tap anywhere (or wait) to close.
 */
import { useEffect, useMemo } from 'react';
import { pathLessons, type PathConfig } from '@sketchling/core';
import { IX, drawingById } from '../lib/content';
import { useLoc, useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { speak, stopSpeaking } from '../lib/tts';
import { useProgress } from '../state/store';
import { DrawingCardArt } from './DrawingView';
import { StoryGuideArt } from './StoryGuide';
import { Confetti } from './Confetti';
import { wait } from '../lib/timing';

const CUE = 'sk-finale';

/** The lesson that completed a world asks the map to play its finale once. */
export function setFinaleCue(world: string) {
  try {
    sessionStorage.setItem(CUE, world);
  } catch {
    /* private mode */
  }
}

/** True (once) when the finale of `world` should play now. */
export function consumeFinaleCue(world: string): boolean {
  try {
    if (sessionStorage.getItem(CUE) !== world) return false;
    sessionStorage.removeItem(CUE);
    return true;
  } catch {
    return false;
  }
}

export function FinaleMoment({ path, onClose }: { path: PathConfig; onClose: () => void }) {
  const t = useT();
  const loc = useLoc();
  const p = useProgress();
  const story = path.story;
  const kind = story?.finale.kind ?? 'party';
  const drawings = useMemo(() => {
    const done = pathLessons(path).filter((l) => l.item.miniUnitId && p.completedMiniUnits.includes(l.item.miniUnitId));
    const ids = [...new Set(done.map((l) => IX.miniUnits.get(l.item.miniUnitId!)?.drawingId).filter((x): x is string => !!x))];
    return ids
      .map((id) => drawingById(id))
      .filter((d): d is NonNullable<typeof d> => !!d)
      .slice(0, 7);
  }, [path, p.completedMiniUnits]);
  useEffect(() => {
    play('unitUnlock');
    if (story) void speak(loc(story.finale.text));
    const id = setTimeout(onClose, wait(6500));
    return () => {
      clearTimeout(id);
      stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!story) return null;
  return (
    <div className="finale-moment" role="dialog" aria-label={t('v2.finaleTitle')} onClick={onClose} data-testid="finale-moment" data-kind={kind}>
      <div className="v2fm-inner">
        <div className={`v2fm-stage v2fm-${kind}`}>
          {kind === 'seasonWheel' && <span className="v2fm-wheel" />}
          {kind === 'party' && <Confetti duration={wait(4000)} count={120} />}
          {kind !== 'seasonWheel' &&
            drawings.map((d, i) => (
              <span key={d.id} className="v2fm-thing" style={{ ['--i' as string]: i }}>
                {kind !== 'lanterns' && <DrawingCardArt drawing={d} />}
              </span>
            ))}
        </div>
        <StoryGuideArt who={story.guide} size={84} className="float" />
        <b className="v2sb-name">{loc(story.title)}</b>
        <p className="v2fm-text">{loc(story.finale.text)}</p>
      </div>
    </div>
  );
}
